/**
 * comparative — the thin FRONTEND callable seam for the L1A "Compare" stage tab
 * (stage key `comparative`, the §C cross-property comparison).
 *
 * Invokes the read-only backend callable `resolveComparativeForProject` (Forge,
 * being built in parallel) and types its response. The contract below is FIXED
 * (agreed with Forge 2026-07-03) — this file builds against it exactly:
 *
 *   input  { clientId, projectId }
 *   output { multi, candidates[], autobestPropertyId, recommendedPropertyId }
 *
 * ── TOLERANCE CONTRACT (same discipline as astroVastu.ts) ────────────────────
 * The callable may not be deployed yet. Every wire field is treated as unknown,
 * the normaliser coerces anything unexpected to a safe default, and a thrown /
 * rejected call resolves to `null` (the tab renders its plain-language error
 * state with a retry). Nothing here may crash the Compare tab.
 *
 * Region + emulator wiring is inherited from the shared `functions` client
 * (asia-south1), identical to every other callable seam (astroVastu.ts,
 * geocode.ts, …).
 *
 * ── DEV FIXTURE (URL-explicit ONLY — owner incident 2026-07-03) ───────────────
 * The fixture applies ONLY when `?cmpFixture=1` is literally present in the URL
 * for THAT page-load — in ALL builds (production AND dev-server). NO
 * localStorage persistence and NO dev-build default: the earlier persisted-flag
 * design let a demo flag stick in the owner's browser, so a later PLAIN url
 * showed SAMPLE data indistinguishable from real data (next to the real
 * recommendation control). A stale legacy key is never read (and is deleted on
 * sight). Fixture responses are stamped `isFixture: true` so the tab banners
 * them as sample data and never mounts real controls against them. The dev
 * harness keeps its own default-on behaviour via its own entry (stub
 * resolvers), not via this flag.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/* ════════════════════ The FIXED response contract ════════════════════ */

/** The three §C verdicts — mirrors the report's traffic-light vocabulary
 *  (green / amber / red per the canonical 3-verdict palette conventions in
 *  auoDisposition.ts). */
export type ComparativeVerdict = 'recommended' | 'acceptable' | 'not_recommended';

/** Entrances (doors & openings) rollup for one candidate. */
export interface ComparativeEntrances {
  favourable: number;
  remedy: number;
  structural: number;
}

/** Rooms & zones rollup for one candidate. */
export interface ComparativeRooms {
  aligned: number;
  remedy: number;
  structural: number;
}

/** Astro-Vastu fit — "X of Y in ideal zone". `null` on the candidate means the
 *  astro reconciliation is resolved at curation (§E), not here. */
export interface ComparativeAstroFit {
  ideal: number;
  total: number;
}

/** Remedy load rollup — total remedies, of which structural. */
export interface ComparativeRemedyLoad {
  total: number;
  structural: number;
}

/* ── Per-item DETAIL (ADDITIVE contract extension, 2026-07-03) — the owner
 *    verifies correctness in the table itself, so every entrance, every AUO
 *    and per-member astro-fit ride along. Absent on the old callable →
 *    `details: null` → the tab renders summary-only (no expanders). ── */

/** One door/opening. Verdict vocabulary matches the summary tallies. */
export interface ComparativeEntranceDetail {
  label: string;
  verdict: 'favourable' | 'remedy' | 'structural';
  note: string | null;
}

/** One AUO (room/zone item). Verdict vocabulary matches the summary tallies. */
export interface ComparativeAuoDetail {
  label: string;
  /** What was checked (e.g. the zone criterion the AUO was judged against). */
  criterion: string;
  verdict: 'aligned' | 'remedy' | 'structural';
  note: string | null;
}

/** One family member's astro-fit line — ideal zone vs the zone matched in
 *  THIS property. `fit` true ⇔ the member's ideal is achievable here. */
export interface ComparativeAstroFitDetail {
  member: string;
  ideal: string;
  matched: string;
  fit: boolean;
}

/** The per-item detail block for one candidate. `astroFit: null` mirrors the
 *  summary's null (resolved at curation §E). */
export interface ComparativeCandidateDetails {
  entrances: ComparativeEntranceDetail[];
  auos: ComparativeAuoDetail[];
  astroFit: ComparativeAstroFitDetail[] | null;
}

/** One evaluated candidate property in the §C comparison. */
export interface ComparativeCandidate {
  /** STABLE registry propertyId (the same id `answers.recommended_property_id`
   *  stores — never the churn-prone candidate slot id). */
  propertyId: string;
  label: string;
  entrances: ComparativeEntrances;
  rooms: ComparativeRooms;
  /** null → the tab shows "Resolved at curation (§E)". */
  astroFit: ComparativeAstroFit | null;
  remedyLoad: ComparativeRemedyLoad;
  verdict: ComparativeVerdict;
  /** Engine's combined-load score (lower = better). Carried for the report;
   *  not an extra visible criterion in the tab. */
  combinedLoad: number;
  /** Per-item detail (additive) — null on the pre-detail callable; the tab
   *  then hides the expanders and renders exactly the old summary view. */
  details: ComparativeCandidateDetails | null;
}

/** The resolved §C comparison for one project. */
export interface ComparativeResolved {
  /** false → fewer than 2 evaluated candidates; the tab explains + links Evaluate. */
  multi: boolean;
  candidates: ComparativeCandidate[];
  /** The engine's pick, null when it has none. */
  autobestPropertyId: string | null;
  /** The staff's SAVED pick (`answers.recommended_property_id`), null when unset. */
  recommendedPropertyId: string | null;
  /** true ⇔ this payload is the `?cmpFixture=1` SAMPLE, not project data. The
   *  tab MUST banner it and MUST NOT mount real controls against it. */
  isFixture: boolean;
  /** Non-null ⇔ the BACKEND compute failed (e.g. "comparative compute
   *  failed: …"). The tab MUST render its error state — never the needs-2+
   *  guidance, which would blame the project's data for an engine crash
   *  (owner-reported on VP678, 2026-07-03). */
  error: string | null;
}

/* ════════════════════ Defensive coercion (wire → total) ════════════════════ */

interface RawResolveResponse {
  multi?: unknown;
  candidates?: unknown;
  autobestPropertyId?: unknown;
  recommendedPropertyId?: unknown;
  error?: unknown;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function normalizeVerdict(v: unknown): ComparativeVerdict {
  if (v === 'recommended' || v === 'acceptable' || v === 'not_recommended') return v;
  // Unknown verdict → the calm middle state, never a false green or red.
  return 'acceptable';
}

function normalizeAstroFit(v: unknown): ComparativeAstroFit | null {
  if (!isObj(v)) return null; // null / absent → "Resolved at curation (§E)"
  return { ideal: num(v.ideal), total: num(v.total) };
}

/* ── detail normalisers (additive; a malformed row is dropped, never crashes) ── */

function normalizeEntranceDetail(v: unknown): ComparativeEntranceDetail | null {
  if (!isObj(v)) return null;
  const label = str(v.label);
  if (!label) return null;
  const verdict =
    v.verdict === 'remedy' || v.verdict === 'structural' ? v.verdict : 'favourable';
  return { label, verdict, note: strOrNull(v.note) };
}

function normalizeAuoDetail(v: unknown): ComparativeAuoDetail | null {
  if (!isObj(v)) return null;
  const label = str(v.label);
  if (!label) return null;
  const verdict =
    v.verdict === 'remedy' || v.verdict === 'structural' ? v.verdict : 'aligned';
  return { label, criterion: str(v.criterion), verdict, note: strOrNull(v.note) };
}

function normalizeAstroFitDetail(v: unknown): ComparativeAstroFitDetail | null {
  if (!isObj(v)) return null;
  const member = str(v.member);
  if (!member) return null;
  return {
    member,
    ideal: str(v.ideal),
    matched: str(v.matched),
    fit: v.fit === true, // boolean contract: fit ⇔ ideal achievable here
  };
}

/** Absent / malformed `details` (the pre-detail callable) → null → the tab
 *  hides the expanders and renders the summary-only view. */
function normalizeDetails(v: unknown): ComparativeCandidateDetails | null {
  if (!isObj(v)) return null;
  return {
    entrances: arr(v.entrances)
      .map(normalizeEntranceDetail)
      .filter((d): d is ComparativeEntranceDetail => d !== null),
    auos: arr(v.auos)
      .map(normalizeAuoDetail)
      .filter((d): d is ComparativeAuoDetail => d !== null),
    astroFit: Array.isArray(v.astroFit)
      ? arr(v.astroFit)
          .map(normalizeAstroFitDetail)
          .filter((d): d is ComparativeAstroFitDetail => d !== null)
      : null,
  };
}

function normalizeCandidate(v: unknown): ComparativeCandidate | null {
  if (!isObj(v)) return null;
  const propertyId = str(v.propertyId);
  const label = str(v.label);
  // A row with neither an id nor a label is unusable — drop it, never render
  // a blank column.
  if (!propertyId && !label) return null;
  const e = isObj(v.entrances) ? v.entrances : {};
  const r = isObj(v.rooms) ? v.rooms : {};
  const rl = isObj(v.remedyLoad) ? v.remedyLoad : {};
  return {
    propertyId: propertyId || label,
    label: label || propertyId,
    entrances: {
      favourable: num(e.favourable),
      remedy: num(e.remedy),
      structural: num(e.structural),
    },
    rooms: {
      aligned: num(r.aligned),
      remedy: num(r.remedy),
      structural: num(r.structural),
    },
    astroFit: normalizeAstroFit(v.astroFit),
    remedyLoad: { total: num(rl.total), structural: num(rl.structural) },
    verdict: normalizeVerdict(v.verdict),
    combinedLoad: num(v.combinedLoad),
    details: normalizeDetails(v.details),
  };
}

/** Coerce any wire payload into a total, safe `ComparativeResolved`. */
export function normalizeComparativeResponse(
  raw: RawResolveResponse | null | undefined,
): ComparativeResolved {
  const r = raw ?? {};
  const candidates = arr(r.candidates)
    .map(normalizeCandidate)
    .filter((c): c is ComparativeCandidate => c !== null);
  return {
    // Trust the backend's multi flag but never claim a comparison the data
    // can't back: multi requires ≥2 usable candidate rows.
    multi: r.multi === true && candidates.length >= 2,
    candidates,
    autobestPropertyId: strOrNull(r.autobestPropertyId),
    recommendedPropertyId: strOrNull(r.recommendedPropertyId),
    isFixture: false, // real wire data is never the sample
    // Carried through verbatim — a non-empty error means the backend compute
    // crashed and the tab must show its error state, not needs-2+.
    error: strOrNull(r.error),
  };
}

/* ════════════════════ Dev fixture (URL-explicit only) ════════════════════ */

/** The RETIRED persistence key (pre-incident builds wrote it). Never read —
 *  only deleted, so a browser that demoed the old build can't stay sticky. */
const LEGACY_FIXTURE_KEY = 'avbp.comparativeFixture';

/**
 * Is the Compare-tab sample fixture enabled for THIS page-load?
 * TRUE only when `?cmpFixture=1` is literally in the current URL — no
 * localStorage, no dev-build default (owner incident 2026-07-03: a persisted
 * demo flag made sample data show on a plain URL). Also deletes the retired
 * legacy persistence key on sight so stale opt-ins from the old build die.
 */
export function comparativeFixtureEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    // Cleanup only — the legacy key is NEVER read as an input.
    window.localStorage.removeItem(LEGACY_FIXTURE_KEY);
  } catch {
    /* private mode — nothing to clean */
  }
  return new URLSearchParams(window.location.search).get('cmpFixture') === '1';
}

/**
 * Realistic dev fixture — 3 candidates covering all three verdicts, an engine
 * pick that DIFFERS from the saved staff pick (exercises the calm dual display
 * in the winner strip), and one null astroFit ("Resolved at curation (§E)").
 * `isFixture` is false HERE because the harness also uses this constant to
 * emulate REAL wire payloads; the seam stamps `isFixture: true` when it serves
 * this as the `?cmpFixture=1` sample.
 */
export const COMPARATIVE_FIXTURE: ComparativeResolved = {
  multi: true,
  isFixture: false,
  error: null,
  candidates: [
    {
      propertyId: 'prop-sunrise-a',
      label: 'Sunrise Villa · Plot A',
      // All-clear lens (0 remedy · 0 structural) so the GREEN cell tone
      // (.cmp-cell.good / .cmp-chip.good) actually renders in the harness
      // alongside amber and red (Vera gate on cb6fd19, Finding 2).
      entrances: { favourable: 3, remedy: 0, structural: 0 },
      rooms: { aligned: 6, remedy: 2, structural: 0 },
      astroFit: { ideal: 3, total: 4 },
      remedyLoad: { total: 3, structural: 0 },
      verdict: 'recommended',
      combinedLoad: 3,
      // Detail rows SUM exactly to the tallies above (3 favourable · 0 · 0;
      // 6 aligned · 2 remedy · 0; astro 3 of 4) — the owner checks this.
      details: {
        entrances: [
          { label: 'Main door · NE', verdict: 'favourable', note: 'Primary entry — auspicious placement' },
          { label: 'Balcony door · E', verdict: 'favourable', note: null },
          { label: 'Utility door · W', verdict: 'favourable', note: null },
        ],
        auos: [
          { label: 'Master bedroom', criterion: 'SW zone', verdict: 'aligned', note: null },
          { label: 'Kitchen', criterion: 'SE zone', verdict: 'aligned', note: null },
          { label: 'Pooja room', criterion: 'NE zone', verdict: 'aligned', note: null },
          { label: 'Living room', criterion: 'N zone', verdict: 'aligned', note: null },
          { label: 'Children’s bedroom', criterion: 'W zone', verdict: 'aligned', note: null },
          { label: 'Study', criterion: 'WSW zone', verdict: 'aligned', note: null },
          { label: 'Toilet', criterion: 'NNW zone', verdict: 'remedy', note: 'Remediable placement' },
          { label: 'Overhead tank', criterion: 'SSW zone', verdict: 'remedy', note: 'Remediable placement' },
        ],
        astroFit: [
          { member: 'Rajesh', ideal: 'SW', matched: 'SW', fit: true },
          { member: 'Priya', ideal: 'SW', matched: 'SW', fit: true },
          { member: 'Aarav', ideal: 'W', matched: 'W', fit: true },
          { member: 'Kamla', ideal: 'NW', matched: 'NE', fit: false },
        ],
      },
    },
    {
      propertyId: 'prop-lakeview-4b',
      label: 'Lakeview Apartment · 4B',
      entrances: { favourable: 2, remedy: 2, structural: 0 },
      rooms: { aligned: 4, remedy: 3, structural: 1 },
      astroFit: { ideal: 2, total: 4 },
      remedyLoad: { total: 6, structural: 1 },
      verdict: 'acceptable',
      combinedLoad: 7,
      // Sums: 2 favourable · 2 remedy · 0; 4 aligned · 3 remedy · 1 structural;
      // astro 2 of 4.
      details: {
        entrances: [
          { label: 'Main door · S', verdict: 'remedy', note: 'Entry placement needs a remedy' },
          { label: 'Kitchen door · SE', verdict: 'remedy', note: null },
          { label: 'Balcony door · E', verdict: 'favourable', note: null },
          { label: 'Terrace door · N', verdict: 'favourable', note: null },
        ],
        auos: [
          { label: 'Master bedroom', criterion: 'SW zone', verdict: 'aligned', note: null },
          { label: 'Kitchen', criterion: 'SE zone', verdict: 'aligned', note: null },
          { label: 'Living room', criterion: 'NNE zone', verdict: 'aligned', note: null },
          { label: 'Study', criterion: 'W zone', verdict: 'aligned', note: null },
          { label: 'Pooja room', criterion: 'SSE zone', verdict: 'remedy', note: 'Remediable placement' },
          { label: 'Guest bedroom', criterion: 'NE zone', verdict: 'remedy', note: null },
          { label: 'Toilet', criterion: 'ENE zone', verdict: 'remedy', note: null },
          { label: 'Kitchen fire', criterion: 'N zone', verdict: 'structural', note: 'Placement is structural — not remediable' },
        ],
        astroFit: [
          { member: 'Rajesh', ideal: 'SW', matched: 'SW', fit: true },
          { member: 'Priya', ideal: 'SW', matched: 'SW', fit: true },
          { member: 'Aarav', ideal: 'W', matched: 'NW', fit: false },
          { member: 'Kamla', ideal: 'NW', matched: 'SE', fit: false },
        ],
      },
    },
    {
      propertyId: 'prop-oldtown-c',
      label: 'Old Town House · Plot C',
      entrances: { favourable: 1, remedy: 2, structural: 2 },
      rooms: { aligned: 3, remedy: 2, structural: 3 },
      astroFit: null, // → "Resolved at curation (§E)"
      remedyLoad: { total: 9, structural: 5 },
      verdict: 'not_recommended',
      combinedLoad: 14,
      // Sums: 1 favourable · 2 remedy · 2 structural; 3 aligned · 2 remedy ·
      // 3 structural; astroFit detail null (mirrors the summary's §E null).
      details: {
        entrances: [
          { label: 'Main door · SW', verdict: 'structural', note: 'Placement is structural — not remediable' },
          { label: 'Side door · SSW', verdict: 'structural', note: null },
          { label: 'Back door · W', verdict: 'remedy', note: null },
          { label: 'Courtyard door · S', verdict: 'remedy', note: null },
          { label: 'Balcony door · E', verdict: 'favourable', note: null },
        ],
        auos: [
          { label: 'Living room', criterion: 'N zone', verdict: 'aligned', note: null },
          { label: 'Study', criterion: 'W zone', verdict: 'aligned', note: null },
          { label: 'Guest bedroom', criterion: 'NW zone', verdict: 'aligned', note: null },
          { label: 'Pooja room', criterion: 'S zone', verdict: 'remedy', note: null },
          { label: 'Overhead tank', criterion: 'SE zone', verdict: 'remedy', note: null },
          { label: 'Master bedroom', criterion: 'NE zone', verdict: 'structural', note: 'Placement is structural — not remediable' },
          { label: 'Kitchen', criterion: 'SW zone', verdict: 'structural', note: null },
          { label: 'Borewell', criterion: 'SSW zone', verdict: 'structural', note: null },
        ],
        astroFit: null,
      },
    },
  ],
  autobestPropertyId: 'prop-sunrise-a',
  recommendedPropertyId: 'prop-lakeview-4b',
};

/* ════════════════════ The callable ════════════════════ */

const _resolveComparative = httpsCallable<
  { clientId: string; projectId: string },
  RawResolveResponse
>(functions, 'resolveComparativeForProject');

/**
 * Resolve the §C comparison for a project. Returns `null` on ANY failure
 * (network, auth, backend error, or a not-yet-deployed callable) so the tab
 * can render its plain-language error state with a retry. Never rejects.
 */
export async function resolveComparativeForProject(
  clientId: string,
  projectId: string,
): Promise<ComparativeResolved | null> {
  if (!clientId || !projectId) return null;
  if (comparativeFixtureEnabled()) {
    // Deep copy so a caller can never mutate the module fixture; stamped as
    // the sample so the tab banners it and withholds real controls.
    const sample = JSON.parse(JSON.stringify(COMPARATIVE_FIXTURE)) as ComparativeResolved;
    sample.isFixture = true;
    return sample;
  }
  try {
    const result = await _resolveComparative({ clientId, projectId });
    return normalizeComparativeResponse(result.data);
  } catch {
    // The callable may not be deployed yet (Forge is building it in parallel).
    // Degrade to null — the tab shows the retryable error state, never a crash.
    return null;
  }
}
